import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { publishableKey: String };
  static targets = ["card", "errors", "submit", "form"];

  async connect() {
    try {
      // Check if form has server-side errors (form was re-rendered after submission)
      this.hasServerErrors =
        this.formTarget.querySelector(".text-red-600") !== null;

      // Lazy-load Stripe only when the signup form is on the page
      const { loadStripe } = await import("@stripe/stripe-js");
      this.stripe = await loadStripe(this.publishableKeyValue);

      if (!this.stripe) {
        throw new Error("Failed to load Stripe");
      }

      // Create Elements instance
      this.elements = this.stripe.elements({
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#0d9488", // teal-600 to match form styling
            colorBackground: "#ffffff",
            colorText: "#374151",
            colorDanger: "#dc2626",
            fontFamily: "system-ui, sans-serif",
            spacingUnit: "4px",
            borderRadius: "6px",
          },
        },
      });

      // Create and mount card element
      this.cardElement = this.elements.create("card", {
        style: {
          base: {
            fontSize: "16px",
            color: "#374151",
            "::placeholder": {
              color: "#9ca3af",
            },
          },
          invalid: {
            color: "#dc2626",
          },
        },
      });

      this.cardElement.mount(this.cardTarget);

      // Listen for card element changes
      this.cardElement.on("change", this.handleCardChange.bind(this));
      this.cardElement.on("ready", this.handleCardReady.bind(this));

      // If there are server errors, reset the submit button state
      if (this.hasServerErrors) {
        this.setSubmitButtonState(true, "Get started");
      }
    } catch (error) {
      console.error("Stripe initialization error:", error);
      this.showError(
        "Failed to initialize payment form. Please refresh and try again.",
      );
    }
  }

  disconnect() {
    if (this.cardElement) {
      this.cardElement.destroy();
    }
  }

  handleCardChange(event) {
    if (event.error) {
      this.showError(event.error.message);
      this.setSubmitButtonState(false, "Complete card details");
    } else {
      this.clearErrors();
      this.setSubmitButtonState(
        event.complete,
        event.complete ? "Get started" : "Complete card details",
      );
    }
  }

  handleCardReady() {
    // Card element is ready for input
    this.cardElement.focus();
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.stripe || !this.cardElement) {
      this.showError("Payment form not ready. Please refresh and try again.");
      return;
    }

    this.setSubmitButtonState(false, "Processing...");
    this.clearErrors();

    try {
      // Create payment method
      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        type: "card",
        card: this.cardElement,
        billing_details: {
          name: this.getFormValue("signup_form_full_name"),
          email: this.getFormValue("signup_form_work_email"),
        },
      });

      if (error) {
        this.showError(error.message);
        this.setSubmitButtonState(true);
        return;
      }

      // Add payment method ID to form
      this.addHiddenField("payment_method_id", paymentMethod.id);

      // Submit the form
      this.formTarget.submit();
    } catch (error) {
      console.error("Payment processing error:", error);
      this.showError("An unexpected error occurred. Please try again.");
      this.setSubmitButtonState(true, "Get started");
    }
  }

  showError(message) {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = message;
      this.errorsTarget.classList.remove("hidden");
    }
  }

  clearErrors() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = "";
      this.errorsTarget.classList.add("hidden");
    }
  }

  // Helper method to check if form has validation errors
  hasFormErrors() {
    return this.formTarget.querySelector(".text-red-600") !== null;
  }

  setSubmitButtonState(enabled, text = null) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !enabled;

      // Use value for input elements, textContent for button elements
      const newText = text || "Get started";

      if (this.submitTarget.tagName === "INPUT") {
        this.submitTarget.value = newText;
      } else {
        this.submitTarget.textContent = newText;
      }

      // Update button styling based on state
      if (enabled) {
        this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed");
        this.submitTarget.classList.add("hover:bg-teal-700", "cursor-pointer");
      } else {
        this.submitTarget.classList.add("opacity-50", "cursor-not-allowed");
        this.submitTarget.classList.remove(
          "hover:bg-teal-700",
          "cursor-pointer",
        );
      }
    }
  }

  getFormValue(fieldName) {
    const field = this.formTarget.querySelector(`[name="${fieldName}"]`);
    return field ? field.value : "";
  }

  addHiddenField(name, value) {
    // Remove existing field if present
    const existing = this.formTarget.querySelector(`input[name="${name}"]`);
    if (existing) {
      existing.remove();
    }

    // Create new hidden field
    const hiddenField = document.createElement("input");
    hiddenField.type = "hidden";
    hiddenField.name = name;
    hiddenField.value = value;
    this.formTarget.appendChild(hiddenField);
  }
}
